import React from 'react';
import { SeatPlanItem, scheduledSeatService } from '../../services/scheduledSeatService';
import { Customer, ScheduledRoute } from '../../types';
import { customerApi } from '../../services/customerApi';

type NotifyType = 'SUCCESS' | 'ERROR' | 'WARNING';

type Params = {
  routes: ScheduledRoute[];
  customers: Customer[];
  operatorDiscountCap: number;
  currentUserRole?: string;
  bookingPolicyMinHoursBeforeDeparture: number;
  load: () => Promise<void>;
  fireNotify: (title: string, message: string, type: NotifyType) => void;
};

export const useScheduledTicketingFlow = ({
  routes,
  customers,
  operatorDiscountCap,
  currentUserRole,
  bookingPolicyMinHoursBeforeDeparture,
  load,
  fireNotify,
}: Params) => {
  const [selectedBooking, setSelectedBooking] = React.useState<any | null>(null);
  const [isSendingBookingEmail, setIsSendingBookingEmail] = React.useState(false);
  const [isSendingBookingWhatsapp, setIsSendingBookingWhatsapp] = React.useState(false);
  const [isDeletingBooking, setIsDeletingBooking] = React.useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);
  const [deleteReason, setDeleteReason] = React.useState('');
  const [deleteConfirmText, setDeleteConfirmText] = React.useState('');
  const [showCancelConfirm, setShowCancelConfirm] = React.useState(false);
  const [cancelReason, setCancelReason] = React.useState('');
  const [cancelPreview, setCancelPreview] = React.useState<any | null>(null);
  const [isPreparingCancelPreview, setIsPreparingCancelPreview] = React.useState(false);
  const [isConfirmingCancel, setIsConfirmingCancel] = React.useState(false);
  const [cancelTargetBookingId, setCancelTargetBookingId] = React.useState<string | null>(null);
  const [bookingThread, setBookingThread] = React.useState<any | null>(null);
  const [bookingThreadLoading, setBookingThreadLoading] = React.useState(false);
  const [bookingThreadError, setBookingThreadError] = React.useState('');
  const [ticketPlan, setTicketPlan] = React.useState<SeatPlanItem | null>(null);
  const [ticketMode, setTicketMode] = React.useState<'HELD' | 'CONFIRMED'>('CONFIRMED');
  const [isIssuingTicket, setIsIssuingTicket] = React.useState(false);
  const [ticketForm, setTicketForm] = React.useState({
    selectedCustomerId: 'MANUAL',
    customerName: '',
    customerEmail: '',
    customerId: '',
    seats: 1,
    unitPriceUSD: 0,
    operatorDiscountPercent: 0,
    holdUntil: '',
    cutoffOverride: false,
    cutoffOverrideReason: '',
    notes: '',
  });
  const [ticketPassengers, setTicketPassengers] = React.useState<Array<{ name: string; idNumber: string }>>([]);
  const [ticketCustomerSnapshot, setTicketCustomerSnapshot] = React.useState<Customer | null>(null);
  const [isLoadingTicketCustomer, setIsLoadingTicketCustomer] = React.useState(false);

  const openIssueTicket = (plan: SeatPlanItem, mode: 'HELD' | 'CONFIRMED' = 'CONFIRMED') => {
    const routePrice = routes.find((r) => r.from === plan.fromCode && r.to === plan.toCode)?.rackRateUSD || 0;
    const resolvedUnitPrice = Number(plan.seatPriceUSD || 0) > 0 ? Number(plan.seatPriceUSD || 0) : Number(routePrice || 0);
    const defaultHoldUntil = new Date(Math.min(new Date(plan.departureAt).getTime(), Date.now() + 2 * 60 * 60 * 1000));
    const holdLocal = Number.isNaN(defaultHoldUntil.getTime())
      ? ''
      : new Date(defaultHoldUntil.getTime() - defaultHoldUntil.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
    setTicketPlan(plan);
    setTicketMode(mode);
    setTicketForm({
      selectedCustomerId: 'MANUAL',
      customerName: '',
      customerEmail: '',
      customerId: '',
      seats: Math.max(1, Math.min(1, Number(plan.availableSeats || 1))),
      unitPriceUSD: Math.max(0, Number(resolvedUnitPrice || 0)),
      operatorDiscountPercent: 0,
      holdUntil: holdLocal,
      cutoffOverride: false,
      cutoffOverrideReason: '',
      notes: '',
    });
    setTicketPassengers([{ name: '', idNumber: '' }]);
  };

  const openBookingDetail = (plan: SeatPlanItem, booking: any) => {
    setSelectedBooking({
      ...booking,
      routeFrom: plan.fromCode,
      routeTo: plan.toCode,
      departureAt: plan.departureAt,
      currencyCode: plan.currencyCode || 'USD',
    });
  };

  const loadBookingThread = React.useCallback(async (bookingId: string) => {
    if (!bookingId) return;
    try {
      setBookingThreadLoading(true);
      setBookingThreadError('');
      const data = await scheduledSeatService.getBookingThread(bookingId);
      setBookingThread(data || { messages: [] });
    } catch (e: any) {
      setBookingThreadError(e?.response?.data?.error || e?.message || 'Failed to load email thread');
      setBookingThread({ messages: [] });
    } finally {
      setBookingThreadLoading(false);
    }
  }, []);

  const sendBookingEmail = async () => {
    if (!selectedBooking?.id) return;
    try {
      setIsSendingBookingEmail(true);
      const payload = await scheduledSeatService.sendBookingEmail(selectedBooking.id);
      const nextBooking = payload?.booking || selectedBooking;
      setSelectedBooking((prev: any) => (prev?.id === nextBooking?.id ? { ...prev, ...nextBooking } : prev));
      fireNotify('Email Sent', 'Ticket email sent to client.', 'SUCCESS');
      await loadBookingThread(selectedBooking.id);
      await load();
    } catch (e: any) {
      fireNotify('Send Failed', e?.response?.data?.error || e?.message || 'Failed to send ticket email', 'ERROR');
    } finally {
      setIsSendingBookingEmail(false);
    }
  };

  const sendBookingWhatsapp = async () => {
    if (!selectedBooking?.id) return;
    try {
      setIsSendingBookingWhatsapp(true);
      const payload = await scheduledSeatService.sendBookingWhatsapp(selectedBooking.id);
      const nextBooking = payload?.booking || selectedBooking;
      setSelectedBooking((prev: any) => (prev?.id === nextBooking?.id ? { ...prev, ...nextBooking } : prev));
      const waUrl = String(payload?.whatsapp?.url || '').trim();
      if (waUrl) window.open(waUrl, '_blank', 'noopener,noreferrer');
      fireNotify('WhatsApp Ready', 'WhatsApp message prepared and opened.', 'SUCCESS');
      await loadBookingThread(selectedBooking.id);
      await load();
    } catch (e: any) {
      fireNotify('Send Failed', e?.response?.data?.error || e?.message || 'Failed to prepare WhatsApp message', 'ERROR');
    } finally {
      setIsSendingBookingWhatsapp(false);
    }
  };

  const updateTicketStatus = async (
    bookingId: string,
    status: 'HELD' | 'CONFIRMED' | 'CANCELLED' | 'EXPIRED',
    opts?: { notes?: string }
  ) => {
    try {
      const updated = await scheduledSeatService.updateBookingStatus(bookingId, { status, notes: opts?.notes });
      const cancellation = (updated?.metadata?.cancellation?.latest || null) as any;
      if (status === 'CANCELLED' && cancellation) {
        const currency = String(cancellation?.currencyCode || 'USD').toUpperCase();
        const penaltyAmount = Number(cancellation?.penaltyAmount || 0);
        const refundableAmount = Number(cancellation?.refundableAmount || 0);
        const penaltyPercent = Number(cancellation?.penaltyPercent || 0);
        fireNotify(
          'Ticket Cancelled',
          `Policy fee ${penaltyPercent}% (${currency} ${penaltyAmount.toFixed(2)}), refundable ${currency} ${refundableAmount.toFixed(2)}`,
          'SUCCESS'
        );
      } else {
        fireNotify('Ticket Updated', `Ticket moved to ${status}`, 'SUCCESS');
      }
      await load();
      setSelectedBooking((prev: any) =>
        prev && prev.id === bookingId
          ? { ...prev, ...(updated || {}), status }
          : prev
      );
      if (status === 'CANCELLED') {
        await loadBookingThread(bookingId);
      }
    } catch (e: any) {
      fireNotify('Update Failed', e?.response?.data?.error || e?.message || 'Failed to update ticket', 'ERROR');
    }
  };

  const openCancelTicketConfirm = async (bookingId: string) => {
    try {
      setIsPreparingCancelPreview(true);
      setCancelTargetBookingId(bookingId);
      setCancelReason('');
      const preview = await scheduledSeatService.getBookingCancellationPreview(bookingId);
      setCancelPreview(preview || null);
      setShowCancelConfirm(true);
    } catch (e: any) {
      fireNotify('Cancellation Preview Failed', e?.response?.data?.error || e?.message || 'Failed to load cancellation policy preview', 'ERROR');
    } finally {
      setIsPreparingCancelPreview(false);
    }
  };

  const confirmCancelTicket = async () => {
    const bookingId = String(cancelTargetBookingId || '').trim();
    if (!bookingId) return;
    const reason = String(cancelReason || '').trim();
    if (!reason) {
      fireNotify('Cancellation Blocked', 'Cancellation reason is required.', 'WARNING');
      return;
    }
    try {
      setIsConfirmingCancel(true);
      await updateTicketStatus(bookingId, 'CANCELLED', { notes: `[CANCEL REASON] ${reason}` });
      setShowCancelConfirm(false);
      setCancelReason('');
      setCancelPreview(null);
      setCancelTargetBookingId(null);
    } finally {
      setIsConfirmingCancel(false);
    }
  };

  const canHardDeleteBooking = React.useMemo(() => {
    const role = String(currentUserRole || '').trim().toUpperCase();
    return role === 'ADMIN' || role === 'SUPER_ADMIN';
  }, [currentUserRole]);

  const hardDeleteTicket = async () => {
    if (!selectedBooking?.id) return;
    if (!canHardDeleteBooking) {
      fireNotify('Access Denied', 'Permanent delete is restricted to Admin.', 'ERROR');
      return;
    }
    const reason = String(deleteReason || '').trim();
    if (String(deleteConfirmText || '').trim().toUpperCase() !== 'DELETE') {
      fireNotify('Delete Blocked', 'Type DELETE to confirm permanent delete.', 'ERROR');
      return;
    }
    if (reason.length < 5) {
      fireNotify('Delete Blocked', 'Delete reason must be at least 5 characters.', 'ERROR');
      return;
    }

    try {
      setIsDeletingBooking(true);
      await scheduledSeatService.deleteBooking(selectedBooking.id, { reason });
      fireNotify('Ticket Deleted', 'Ticket was permanently deleted.', 'SUCCESS');
      setShowDeleteConfirm(false);
      setDeleteReason('');
      setDeleteConfirmText('');
      setSelectedBooking(null);
      setBookingThread(null);
      await load();
    } catch (e: any) {
      fireNotify('Delete Failed', e?.response?.data?.error || e?.message || 'Failed to delete ticket', 'ERROR');
    } finally {
      setIsDeletingBooking(false);
    }
  };

  const selectedTicketCustomer = React.useMemo(() => {
    if (ticketForm.selectedCustomerId === 'MANUAL') return null;
    const live = ticketCustomerSnapshot && ticketCustomerSnapshot.id === ticketForm.selectedCustomerId ? ticketCustomerSnapshot : null;
    if (live) return live;
    return customers.find((c) => c.id === ticketForm.selectedCustomerId) || null;
  }, [ticketForm.selectedCustomerId, customers, ticketCustomerSnapshot]);

  const resolveEffectiveScheduleDiscount = (customer: Customer | null) => {
    if (!customer) return { percent: 0, source: 'NONE' as const };
    const direct = Number((customer as any)?.scheduleDiscount ?? 0);
    const categoryDefault = Number((customer as any)?.categoryAssignments?.[0]?.Category?.defaultScheduleDiscount ?? 0);
    const commercialFallback = Number((customer as any)?.commercialTerms?.scheduleDiscount ?? 0);
    const percent = Math.max(0, direct > 0 ? direct : categoryDefault > 0 ? categoryDefault : commercialFallback);
    const source = direct > 0 ? 'CUSTOMER' : categoryDefault > 0 ? 'CATEGORY' : commercialFallback > 0 ? 'COMMERCIAL' : 'NONE';
    return { percent, source };
  };

  const customerDiscountPercent = React.useMemo(() => {
    return resolveEffectiveScheduleDiscount(selectedTicketCustomer).percent;
  }, [selectedTicketCustomer]);

  const customerDiscountSource = React.useMemo(() => {
    return resolveEffectiveScheduleDiscount(selectedTicketCustomer).source;
  }, [selectedTicketCustomer]);

  const ticketPricing = React.useMemo(() => {
    const seats = Math.max(1, Number(ticketForm.seats || 1));
    const rackUnit = Math.max(0, Number(ticketForm.unitPriceUSD || 0));
    const rackTotal = rackUnit * seats;
    const cappedOperatorDiscountPercent = Math.max(0, Math.min(Number(ticketForm.operatorDiscountPercent || 0), Number(operatorDiscountCap || 0)));
    const customerDiscountAmount = (rackTotal * customerDiscountPercent) / 100;
    const operatorDiscountAmount = (rackTotal * cappedOperatorDiscountPercent) / 100;
    const netTotal = Math.max(0, rackTotal - customerDiscountAmount - operatorDiscountAmount);
    const netUnitPrice = seats > 0 ? netTotal / seats : 0;
    return {
      rackUnit,
      rackTotal,
      cappedOperatorDiscountPercent,
      customerDiscountAmount,
      operatorDiscountAmount,
      netTotal,
      netUnitPrice,
    };
  }, [ticketForm.seats, ticketForm.unitPriceUSD, ticketForm.operatorDiscountPercent, customerDiscountPercent, operatorDiscountCap]);

  const ticketBookingWindow = React.useMemo(() => {
    if (!ticketPlan?.departureAt) {
      return {
        minHoursBeforeDeparture: Math.max(0, Number(bookingPolicyMinHoursBeforeDeparture || 0)),
        cutoffAt: null as Date | null,
        remainingMinutes: null as number | null,
        isClosed: false,
      };
    }
    const departure = new Date(ticketPlan.departureAt);
    const minHours = Math.max(0, Number(bookingPolicyMinHoursBeforeDeparture || 0));
    const cutoffAt = new Date(departure.getTime() - minHours * 60 * 60 * 1000);
    const remainingMinutes = Math.floor((cutoffAt.getTime() - Date.now()) / 60000);
    return {
      minHoursBeforeDeparture: minHours,
      cutoffAt,
      remainingMinutes,
      isClosed: remainingMinutes < 0,
    };
  }, [ticketPlan, bookingPolicyMinHoursBeforeDeparture]);

  const submitIssueTicket = async () => {
    if (!ticketPlan) return;
    if (ticketBookingWindow.isClosed && !ticketForm.cutoffOverride) {
      fireNotify('Booking Window Closed', 'Enable cutoff override and provide a reason (authorized roles only).', 'ERROR');
      return;
    }
    if (ticketBookingWindow.isClosed && ticketForm.cutoffOverride && !String(ticketForm.cutoffOverrideReason || '').trim()) {
      fireNotify('Issue Ticket Failed', 'Cutoff override reason is required', 'ERROR');
      return;
    }
    const seats = Math.max(1, Number(ticketForm.seats || 1));
    const holdUntilIso = ticketMode === 'HELD' && ticketForm.holdUntil ? new Date(ticketForm.holdUntil).toISOString() : undefined;
    if (ticketMode === 'HELD' && !holdUntilIso) {
      fireNotify('Hold Failed', 'Hold until date/time is required', 'ERROR');
      return;
    }
    if (seats > Number(ticketPlan.availableSeats || 0)) {
      fireNotify('Issue Ticket Failed', `Only ${ticketPlan.availableSeats} seat(s) available`, 'ERROR');
      return;
    }
    if (!ticketForm.customerName.trim()) {
      fireNotify('Issue Ticket Failed', 'Customer name is required', 'ERROR');
      return;
    }
    if (Number(ticketForm.unitPriceUSD || 0) < 0) {
      fireNotify('Issue Ticket Failed', 'Unit price cannot be negative', 'ERROR');
      return;
    }
    const invalidPassenger = ticketPassengers.find((p) => !String(p.name || '').trim());
    if (invalidPassenger) {
      fireNotify('Issue Ticket Failed', 'Please fill all passenger names', 'ERROR');
      return;
    }

    try {
      setIsIssuingTicket(true);
      await scheduledSeatService.createBooking({
        inventoryId: ticketPlan.id,
        customerId: ticketForm.customerId.trim() || undefined,
        status: ticketMode,
        seats,
        unitPriceUSD: Number(ticketPricing.netUnitPrice || 0),
        totalPriceUSD: Number(ticketPricing.netTotal || 0),
        currencyCode: ticketPlan.currencyCode || 'USD',
        holdUntil: holdUntilIso,
        cutoffOverride: !!ticketForm.cutoffOverride,
        cutoffOverrideReason: ticketForm.cutoffOverride ? String(ticketForm.cutoffOverrideReason || '').trim() : undefined,
        notes: ticketForm.notes.trim() || undefined,
        metadata: {
          selectedCustomerId: ticketForm.selectedCustomerId === 'MANUAL' ? null : ticketForm.selectedCustomerId,
          customerName: ticketForm.customerName.trim(),
          customerEmail: ticketForm.customerEmail.trim() || null,
          discount: {
            customerPercent: customerDiscountPercent,
            operatorPercent: ticketPricing.cappedOperatorDiscountPercent,
            customerAmount: ticketPricing.customerDiscountAmount,
            operatorAmount: ticketPricing.operatorDiscountAmount,
            netUnitPrice: ticketPricing.netUnitPrice,
            netTotal: ticketPricing.netTotal,
          },
          passengers: ticketPassengers.map((p, idx) => ({
            index: idx + 1,
            name: String(p.name || '').trim(),
            idNumber: String(p.idNumber || '').trim() || null,
          })),
          issuedFrom: 'SCHEDULED_CALENDAR_DAY_OPTIONS',
        },
      });
      fireNotify(ticketMode === 'HELD' ? 'Seat Held' : 'Ticket Issued', `${ticketPlan.fromCode} -> ${ticketPlan.toCode} for ${ticketForm.customerName}`, 'SUCCESS');
      try {
        window.dispatchEvent(new CustomEvent('ma:schedule-ticket-issued'));
      } catch {
        // ignore browser event errors
      }
      setTicketPlan(null);
      await load();
    } catch (e: any) {
      fireNotify('Issue Ticket Failed', e?.response?.data?.error || e?.message || 'Failed to issue ticket', 'ERROR');
    } finally {
      setIsIssuingTicket(false);
    }
  };

  React.useEffect(() => {
    const seats = Math.max(1, Number(ticketForm.seats || 1));
    setTicketPassengers((prev) => {
      if (prev.length === seats) return prev;
      const next = [...prev];
      while (next.length < seats) next.push({ name: '', idNumber: '' });
      return next.slice(0, seats);
    });
  }, [ticketForm.seats]);

  React.useEffect(() => {
    const customerId = String(ticketForm.selectedCustomerId || '').trim();
    if (!customerId || customerId === 'MANUAL') {
      setTicketCustomerSnapshot(null);
      setIsLoadingTicketCustomer(false);
      return;
    }
    let active = true;
    setIsLoadingTicketCustomer(true);
    customerApi
      .getById(customerId)
      .then((full) => {
        if (!active) return;
        setTicketCustomerSnapshot(full || null);
      })
      .catch(() => {
        if (!active) return;
        setTicketCustomerSnapshot(null);
      })
      .finally(() => {
        if (!active) return;
        setIsLoadingTicketCustomer(false);
      });
    return () => {
      active = false;
    };
  }, [ticketForm.selectedCustomerId]);

  React.useEffect(() => {
    if (!selectedBooking?.id) {
      setBookingThread(null);
      setBookingThreadError('');
      return;
    }
    loadBookingThread(selectedBooking.id);
  }, [selectedBooking?.id, loadBookingThread]);

  return {
    bookingThread,
    bookingThreadError,
    bookingThreadLoading,
    cancelPreview,
    cancelReason,
    confirmCancelTicket,
    canHardDeleteBooking,
    customerDiscountPercent,
    customerDiscountSource,
    deleteConfirmText,
    deleteReason,
    hardDeleteTicket,
    isDeletingBooking,
    isIssuingTicket,
    isLoadingTicketCustomer,
    isPreparingCancelPreview,
    isConfirmingCancel,
    isSendingBookingEmail,
    isSendingBookingWhatsapp,
    loadBookingThread,
    openBookingDetail,
    openCancelTicketConfirm,
    openIssueTicket,
    resolveEffectiveScheduleDiscount,
    selectedBooking,
    selectedTicketCustomer,
    sendBookingEmail,
    sendBookingWhatsapp,
    setDeleteConfirmText,
    setDeleteReason,
    setCancelReason,
    setSelectedBooking,
    setShowCancelConfirm,
    setShowDeleteConfirm,
    setTicketForm,
    setTicketMode,
    setTicketPassengers,
    setTicketPlan,
    showDeleteConfirm,
    showCancelConfirm,
    submitIssueTicket,
    ticketBookingWindow,
    ticketCustomerSnapshot,
    ticketForm,
    ticketMode,
    ticketPassengers,
    ticketPlan,
    ticketPricing,
    updateTicketStatus,
  };
};
